// client/src/components/layout/Sidebar.jsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome,
  FiKey,
  FiUser,
  FiChevronDown,
  FiGrid,
  FiClock,
  FiActivity,
  FiTerminal,
  FiPieChart,
  FiSend
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import BrandLogo from '../../components/common/BrandLogo';
import ThemeSwitcher from '../../components/common/ThemeSwitcher';

// 菜单权限白名单(与后端 validators 一致): 普通用户需超管开通后侧边栏才显示对应板块
// admin 角色永远全量; 个人资料无需授权
const MENU_PERMISSIONS = ['dashboard', 'statistics', 'monitor', 'commands', 'signals', 'licenses', 'system'];

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { settings } = useSettings();

  // 品牌标题(读取系统设置): 用户级贴牌 brandTitle 优先, 其次全局默认贴牌, 最后平台标题
  const systemTitle = settings?.systemTitle || 'EA运维中心';
  const displayTitle = currentUser?.brandTitle || settings?.brandTitle || systemTitle;
  const systemLogo = settings?.systemLogo || '';

  // 侧边栏两级结构: 板块(一级) -> 页面(二级)
  // perm: 普通用户需要的菜单权限, 无 perm 则永远显示(如个人资料)
  const routes = [
    {
      path: '/dashboard',
      name: '平台总览',
      icon: <FiHome />,
      perm: 'dashboard'
    },
    {
      path: '/statistics',
      name: '数据统计',
      icon: <FiPieChart />,
      perm: 'statistics',
      subRoutes: [
        { path: '/statistics', name: '统计分析' }
      ]
    },
    {
      path: '/monitor/instances',
      name: '实时监控',
      icon: <FiActivity />,
      perm: 'monitor',
      subRoutes: [
        { path: '/monitor/instances', name: '实例总览' }
      ]
    },
    {
      path: '/commands',
      name: '远程指令',
      icon: <FiTerminal />,
      perm: 'commands',
      subRoutes: [
        { path: '/commands', name: '指令中心' },
        { path: '/commands/history', name: '执行记录' }
      ]
    },
    {
      path: '/signals',
      name: '云端下发',
      icon: <FiSend />,
      perm: 'signals',
      subRoutes: [
        { path: '/signals', name: '信号记录' },
        { path: '/signal-sources', name: '策略源管理' }
      ]
    },
    {
      path: '/applications',
      name: '授权管理',
      icon: <FiKey />,
      perm: 'licenses',
      subRoutes: [
        { path: '/applications', name: '应用列表' },
        { path: '/licenses', name: '授权列表' },
        { path: '/licenses/create', name: '生成授权码' },
        { path: '/licenses/applications', name: '授权申请', icon: <FiClock /> }
      ]
    },
    {
      path: '/users',
      name: '系统管理',
      icon: <FiGrid />,
      perm: 'system',
      subRoutes: [
        { path: '/users', name: '用户管理', adminOnly: true },
        { path: '/security', name: '安全审计', adminOnly: true },
        // 系统设置仅超管(代理改贴牌走左下角"页面风格"入口, 不占系统管理)
        { path: '/settings', name: '系统设置', adminOnly: true },
        { path: '/api-docs', name: 'API 文档' }
      ]
    },
    {
      path: '/profile',
      name: '个人资料',
      icon: <FiUser />
    }
  ];

  // 侧边栏动画
  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    closed: {
      x: '-100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  // 路由匹配: 精确匹配 或 前缀匹配(/licenses 命中 /licenses/123)
  const isActive = (path) => {
    if (location.pathname === path) return true;
    return location.pathname.startsWith(path + '/');
  };

  // 渲染菜单项
  const SidebarMenuItem = ({ route, isOpen, setIsOpen }) => {
    const visibleSubRoutes = route.subRoutes
      ? route.subRoutes.filter(sub => !sub.adminOnly || currentUser?.role === 'admin')
      : [];

    const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(
      visibleSubRoutes.some(subRoute => isActive(subRoute.path))
    );

    const toggleSubMenu = () => {
      if (visibleSubRoutes.length > 0) {
        setIsSubMenuOpen(!isSubMenuOpen);
      }
    };

    const isMenuActive = isActive(route.path);

    return (
      <div className="mb-1">
        {visibleSubRoutes.length > 0 ? (
          <>
            <button
              onClick={toggleSubMenu}
              className={`flex items-center w-full py-2 px-3 rounded-md transition-colors duration-150 text-sm ${
                isMenuActive
                  ? 'bg-[#ddf4ff] text-[#0969da] font-medium'
                  : 'text-[#24292f] hover:bg-[#f6f8fa]'
              }`}
            >
              <span className="text-base mr-3">{route.icon}</span>
              <span className="flex-1 text-left">{route.name}</span>
              {route.badge && (
                <span className="mr-2 text-[10px] px-1.5 py-0.5 rounded bg-[#ddf4ff] text-[#0969da] border border-[#b6e3ff]">
                  {route.badge}
                </span>
              )}
              <motion.span
                animate={{ rotate: isSubMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FiChevronDown className="text-[#8b949e]" />
              </motion.span>
            </button>

            <AnimatePresence>
              {isSubMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="ml-6 mt-1 border-l border-[#d0d7de] pl-4 space-y-1"
                >
                  {visibleSubRoutes.map((subRoute, idx) => (
                    <Link
                      key={idx}
                      to={subRoute.path}
                      onClick={() => setIsOpen(false)}
                      className={`block py-1.5 px-2 rounded text-sm transition-colors duration-150 ${
                        isActive(subRoute.path)
                          ? 'text-[#0969da] font-medium'
                          : 'text-[#57606a] hover:text-[#1f2328] hover:bg-[#f6f8fa]'
                      }`}
                    >
                      {subRoute.name}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <Link
            to={route.path}
            onClick={() => setIsOpen(false)}
            className={`flex items-center py-2 px-3 rounded-md transition-colors duration-150 text-sm ${
              isActive(route.path)
                ? 'bg-[#ddf4ff] text-[#0969da] font-medium'
                : 'text-[#24292f] hover:bg-[#f6f8fa]'
            }`}
          >
            <span className="text-base mr-3">{route.icon}</span>
            <span>{route.name}</span>
          </Link>
        )}
      </div>
    );
  };

  const SidebarBrand = ({ compact = false }) => (
    <div className="flex items-center justify-center h-16 border-b border-[#d0d7de] gap-2 px-4">
      {systemLogo ? (
        <img src={systemLogo} alt="logo" className="h-7 w-7 rounded object-contain shrink-0" />
      ) : (
        <BrandLogo size={28} />
      )}
      <div className="text-lg font-semibold gradient-text truncate">
        {displayTitle}
      </div>
    </div>
  );

  const SidebarMenu = () => {
    // 普通用户按超管开通的菜单权限过滤; admin 角色全量
    const userPerms = currentUser?.role === 'admin'
      ? MENU_PERMISSIONS
      : (Array.isArray(currentUser?.menuPermissions) ? currentUser.menuPermissions : []);

    return (
      <div className="px-3 py-4">
        {routes.map((route, index) => {
          // 菜单权限过滤: 未开通的板块直接不渲染
          if (route.perm && !userPerms.includes(route.perm)) {
            return null;
          }
          // 过滤后无可见子项的分组不渲染(如普通用户看不到 用户管理/系统设置 时隐藏系统管理)
          if (route.subRoutes) {
            const visible = route.subRoutes.filter(sub =>
              !sub.adminOnly || currentUser?.role === 'admin');
            if (visible.length === 0) return null;
          }
          return (
            <SidebarMenuItem
              key={index}
              route={route}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          );
        })}
      </div>
    );
  };

  return (
    <>
      {/* 桌面版侧边栏 */}
      <div className="hidden md:flex md:flex-col md:w-64 bg-white/90 border-r border-[#d0d7de]">
        <div className="flex flex-col flex-1 overflow-y-auto">
          <SidebarBrand />
          <SidebarMenu />
        </div>
        <ThemeSwitcher />
      </div>

      {/* 移动版侧边栏 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden fixed inset-y-0 left-0 z-20 w-64 bg-white/90 border-r border-[#d0d7de] shadow-md"
            variants={sidebarVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <div className="flex flex-col flex-1 overflow-y-auto">
              <SidebarBrand />
              <SidebarMenu />
            </div>
            <ThemeSwitcher />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
